import { callApi } from './api';
import Course33Media, { Course33MediaSlot } from '@/models/course-33-media';
import { MediaTypes } from '@/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Admin CRUD for the course-33 media collection (Day-0 videos + morning/night
// audio). Both admin sections share these endpoints; they differ only in the
// (day, slot) they write.
const course33MediaUrl = import.meta.env.VITE_API_URL_V1 + '/admin/course-33/media';

const MEDIA_KEY = ['course-33-media'];

export const getCourse33Media = async () => {
    return callApi<Course33Media[]>({ url: course33MediaUrl, method: 'GET' });
};

export const useCourse33Media = () => {
    return useQuery({ queryKey: MEDIA_KEY, queryFn: getCourse33Media });
};

/** Only editable fields go over the wire (the API's validate() rejects unmatched keys). */
export type Course33MediaPayload = {
    day: number;
    slot: Course33MediaSlot;
    title: string;
    caption?: string;
    mediaPath: string;
    mediaType: MediaTypes;
    orderIndex?: number;
};

export const createCourse33Media = async (payload: Course33MediaPayload) => {
    return callApi<Course33Media>({ url: course33MediaUrl, method: 'POST', data: payload });
};

export const useCreateCourse33Media = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createCourse33Media,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: MEDIA_KEY }),
    });
};

export const updateCourse33Media = async ({ mediaId, ...payload }: Partial<Course33MediaPayload> & { mediaId: string }) => {
    return callApi<Course33Media>({ url: `${course33MediaUrl}/${mediaId}`, method: 'PUT', data: payload });
};

export const useUpdateCourse33Media = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateCourse33Media,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: MEDIA_KEY }),
    });
};

export const deleteCourse33Media = async ({ mediaId }: { mediaId: string }) => {
    return callApi({ url: `${course33MediaUrl}/${mediaId}`, method: 'DELETE' });
};

export const useDeleteCourse33Media = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteCourse33Media,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: MEDIA_KEY }),
    });
};
